import { AppVersion, baseURL, post } from '../base'
import { streamToText } from '../utils/streamToText'
import { useDebugStore } from '@/store/debug'
import { getCurrentTime } from '@/utils/getCurrentTime'
import { useUserStore } from '@/store/user'
import type {
  Character,
  Message,
  Role,
} from '@/types'
import type { ChatHistoryResponse } from '@/types/chat'
import { textToMessagesNew } from '@/utils/textToMessages'
import type { GenerateEventPayload } from '@/types/event'
import type { EventTrackRequest } from '@/types/eventTrack'

export interface Msg {
  name: string
  message: string
  role: Role
  charOrUserId?: number | null
  action?: string | null
  expression?: string | null
}

export interface ChatHistoryPayload {
  role: string
  content: string
}

export interface GetCharResponsesPayload {
  universeId: number
  eventOrder: number
  chatHistory?: ChatHistoryPayload[]
  enableVoice?: boolean
  preview?: boolean
}

interface GetCharResponsesResult {
  universeId: number
  messages: Omit<Msg, 'name'>[]
}

class ChatService {
  async getCharResponses(
    payload: GetCharResponsesPayload,
  ): Promise<GetCharResponsesResult> {
    return post<GetCharResponsesResult>('/chat/getCharResponses', payload)
  }

  private async fetchCharResponses(
    payload: GetCharResponsesPayload,
    signal?: AbortSignal,
  ) {
    const userStore = useUserStore()
    return await fetch(`${baseURL}/chat/getResponseStream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization':
          userStore.accessToken !== '' ? `Bearer ${userStore.accessToken}` : '',
        'App-Version': AppVersion,
      },
      body: JSON.stringify(payload),
      signal,
    })
  }

  private async getEventStory(
    payload: GenerateEventPayload,
    signal?: AbortSignal,
  ) {
    const userStore = useUserStore()
    return await fetch(`${baseURL}/chat/generateEventStory`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization':
          userStore.accessToken !== '' ? `Bearer ${userStore.accessToken}` : '',
        'App-Version': AppVersion,
      },
      body: JSON.stringify(payload),
      signal,
    })
  }

  async getCharResponsesStream(
    payload: GetCharResponsesPayload,
    onChunkReceived: (chunk: string) => void,
    signal?: AbortSignal,
  ) {
    const userStore = useUserStore()
    const debugStore = useDebugStore()
    let response = await this.fetchCharResponses(payload, signal)

    if (response.status === 403) {
      const responseToJson = await response.json()
      if (responseToJson.detail.sub_error_code === 4031) {
        await userStore.RefreshToken()
        response = await this.fetchCharResponses(payload, signal)
        if (response.status === 403) {
          userStore.logOut()
        }
      }
      else {
        userStore.logOut()
      }
    }
    if (!response.ok) {
      throw new Error(response.statusText)
    }
    const requestId: string | null = response.headers.get('request-id')
    if (requestId) {
      debugStore.requestIds.push({ time: getCurrentTime(), requestId })
    }
    await streamToText(response.body, onChunkReceived)
  }

  async getEventStoryStream(
    payload: GenerateEventPayload,
    onChunkReceived: (chunk: string) => void,
    signal?: AbortSignal,
  ) {
    const userStore = useUserStore()
    const debugStore = useDebugStore()
    let response = await this.getEventStory(payload, signal)

    if (response.status === 403) {
      const responseToJson = await response.json()
      if (responseToJson.detail.sub_error_code === 4031) {
        await userStore.RefreshToken()
        response = await this.fetchCharResponses(payload, signal)
        if (response.status === 403) {
          userStore.logOut()
        }
      }
      else {
        userStore.logOut()
      }
    }
    if (!response.ok) {
      throw new Error(response.statusText)
    }
    const requestId: string | null = response.headers.get('request-id')
    if (requestId) {
      debugStore.requestIds.push({ time: getCurrentTime(), requestId })
    }
    await streamToText(response.body, onChunkReceived)
  }

  /**
   * Added in v1.2
   * get the chat history when fetching new universe
   * assign the response to the chat object
   * as messages and rawXML
   */
  async fetchChatHistory(
    universeId: number,
    chars: Character[],
  ): Promise<Message[]> {
    const res = await post<{
      sessionId: string
      chatHistory: ChatHistoryResponse[]
    }>(
      '/universe/v2/getChatHistory',
      {
        universeId,
      },
    )
    const messages: Message[] = []

    // sort the chat history by createTime
    res.chatHistory.sort((a, b) => a.createTime! - b.createTime!)

    // unless the first message is the initialized message
    // we parse the rest of the chat history messages
    res.chatHistory.slice(1).forEach((history) => {
      const parsed = textToMessagesNew(history.message, chars)
      messages.push(...parsed)
    })
    return messages
  }

  /**
   * Added in v1.2
   * check if the chat has ended
   * @param universeId number
   */
  async checkChatStatus(
    universeId: number,
    roundNumber: number,
    eventsOrder: number,
  ): Promise<boolean> {
    const res = await post<{
      status: boolean
    }>(
      '/chat/checkChatStatus',
      {
        universeId,
        roundNumber,
        eventsOrder,
      },
    )
    return res.status
  }

  async reportEvent(payload: EventTrackRequest) {
    return post('https://fsr70cghb7.execute-api.us-east-1.amazonaws.com/dev/push_data', payload)
  }

  /**
   *
   * @param images \base64 images array
   * @param voiceList aurio_url array
   * @returns void
   */
  async createVideo(images: string[], voiceList: string[], bgm: string) {
    const form = new FormData()
    form.append('files', JSON.stringify(images))
    form.append('voice_list', JSON.stringify(voiceList))
    form.append('gap_time', '0.1')
    form.append('bgm_url', bgm)
    return post('/video/linkVideoFromImage', form, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 180 * 1000,
    })
  }
}

export const chatService = new ChatService()
